import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, ChevronDown, Loader2, MapPin, X } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { apiResource } from "../../services/api";
import { useAppDispatch } from "../../hooks/useStore";
import { createPartner } from "../../store/slices/partnersSlice";
import { createAddress } from "../../store/slices/addressesSlice";
import { Modal } from "../UI/Modal";
import { PartnerForm } from "../partners/PartnerForm";
import type {
  EntitySnapshot,
  Partner,
  PartnerInfoWithClientAndReg,
  PartnerInfoWithClientCode,
} from "../../types";

/** Which directory backs the list — Partners (companies) or Addresses (terminals). */
export type DirectorySource = "partners" | "addresses";

/** Whatever PartnerForm hands back — narrowed per directory at dispatch. */
type DirectoryFormData =
  | PartnerInfoWithClientAndReg
  | PartnerInfoWithClientCode
  | Partial<Partner>;

interface DirectorySelectProps {
  source: DirectorySource;
  /** The directory record currently picked; `null` while nothing is selected. */
  selectedId?: string | null;
  /** Name of the current selection — what the closed box shows. */
  value: string;
  /** Fires only on a real pick from the list (or straight after a quick-add). */
  onSelect: (snapshot: DirectorySelection) => void;
  /** Fires on the X — the parent wipes the whole block it filled from the pick. */
  onClear: () => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  size?: "sm" | "md";
  /** Hide the Add button where creating a directory record is not wanted. */
  allowCreate?: boolean;
  /**
   * What the Add form starts with — a company the page already knows about but
   * the directory does not yet. The record it creates is selected as usual.
   */
  createDefaults?: Partial<Partner>;
}

/**
 * A snapshot this picker emits.
 *
 * `EntitySnapshot._id` is optional because the type is shared with the
 * temporary, hand-built snapshots a form holds for a company that is not in the
 * directory at all. This picker never emits one of those: `onSelect` fires only
 * from `toSnapshot(record)` — for a record read from the directory, or one just
 * written to it by the quick-add — and `Partner._id` is required. So the id is
 * always there, and saying so here spares every consumer a guard for a case
 * that cannot happen.
 */
export type DirectorySelection = EntitySnapshot & { _id: string };

const PAGE_SIZE = 10;

/**
 * A directory picker where the selection — not the text — is the value.
 *
 * The box is a search field, but nothing typed into it ever leaves the
 * component: `onSelect` fires only for a row picked from the list, and closing
 * the dropdown throws the search text away and repaints the box with whatever
 * is actually selected. So a half-typed company name can never survive a blur,
 * and the form can never hold a name that has no record behind it.
 *
 * Both directories are paged rather than fuzzy-matched (the /suggestions
 * endpoints cap at ten and need two characters before they answer at all): the
 * list opens on the first page of everything, narrows as the user types, and
 * grows by a page at a time through "Load more".
 *
 * Add opens the same directory form the Partners / Addresses pages use, and
 * selects the record it creates — otherwise a client not yet in the directory
 * would be a dead end, since typing one is no longer possible.
 */
export const DirectorySelect: React.FC<DirectorySelectProps> = ({
  source,
  selectedId,
  value,
  onSelect,
  onClear,
  placeholder,
  disabled = false,
  error = false,
  size = "sm",
  allowCreate = true,
  createDefaults,
}) => {
  const { t } = useTranslation(["common", "partners", "addresses"]);
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState<Partner[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  /**
   * Only the newest request may write to state. Typing fires a fresh page-1
   * load on every debounce tick, and a slow earlier response landing last
   * would otherwise repaint the list with results for a stale search term.
   */
  const requestRef = useRef(0);
  /** Opening loads page 1 itself; the debounce must not repeat that request. */
  const skipDebounceRef = useRef(false);

  const load = useCallback(
    async (search: string, nextPage: number, append: boolean) => {
      const requestId = ++requestRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const response = await apiResource(source).getAll({
          search: search.trim(),
          page: nextPage,
          limit: PAGE_SIZE,
        });
        if (requestId !== requestRef.current) return;

        const data = response.data.data;
        const rows: Partner[] =
          (source === "partners" ? data.partners : data.addresses) ?? [];

        setRecords((prev) => (append ? [...prev, ...rows] : rows));
        setPage(data.pagination?.page ?? nextPage);
        setPages(data.pagination?.pages ?? 0);
      } catch {
        if (requestId !== requestRef.current) return;
        // A failed lookup leaves the list as it was rather than blanking it;
        // the box stays open so the user can simply try again.
        if (!append) setRecords([]);
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [source],
  );

  const open = useCallback(() => {
    if (disabled || isOpen) return;
    skipDebounceRef.current = true;
    setIsOpen(true);
    setQuery("");
    setRecords([]);
    setPage(1);
    setPages(0);
    load("", 1, false);
  }, [disabled, isOpen, load]);

  /**
   * Closing is what discards the typed text: the box only shows `query` while
   * it is open, so reverting to the selected name is automatic.
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  // Debounced re-search. Skipped on the tick that opened the box, which has
  // already fetched page 1 itself.
  useEffect(() => {
    if (!isOpen) return;
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(query, 1, false), 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isOpen]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [close]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleSelect = (record: Partner) => {
    onSelect(toSnapshot(record));
    close();
  };

  /** Wipes the selection and closes; clicking the box reopens the list. */
  const handleClear = () => {
    onClear();
    close();
  };

  /** Hides the dialog; PartnerForm unmounts with it, so its state is reset. */
  const closeCreate = () => {
    setShowCreate(false);
    setCreating(false);
  };

  const handleCreate = async (data: DirectoryFormData) => {
    setCreating(true);
    try {
      const created: Partner = await (source === "partners"
        ? dispatch(createPartner(data as PartnerInfoWithClientAndReg)).unwrap()
        : dispatch(createAddress(data as PartnerInfoWithClientCode)).unwrap());

      // A record created here exists to be used here — select it straight away
      // rather than making the user find it in a list they just left. Nothing
      // else on the page moves: the order itself is untouched and unsaved.
      onSelect(toSnapshot(created));
      closeCreate();
      toast.success(
        t(
          source === "partners"
            ? "common:directorySelect.partnerCreated"
            : "common:directorySelect.addressCreated",
        ),
      );
      // Pull the directory again so the list this field searches includes the
      // new record straight away, instead of showing a page fetched before it
      // existed.
      setQuery("");
      setPage(1);
      load("", 1, false);
    } catch (err: unknown) {
      // The create thunks reject with the API's own message as a plain string.
      toast.error(
        typeof err === "string"
          ? err
          : t("common:directorySelect.createFailed"),
      );
    } finally {
      setCreating(false);
    }
  };

  const searchPlaceholder = t(
    source === "partners"
      ? "common:directorySelect.searchPartners"
      : "common:directorySelect.searchAddresses",
  );

  const sizeCls =
    size === "sm" ? "h-8 px-2.5 py-1 text-[13px]" : "h-10 px-3 py-2 text-sm";
  // One border-colour per state rather than layering, for the reason the Input
  // in FormField documents: compiled-CSS ordering, not className ordering,
  // decides which of two border classes wins.
  const boxState = error
    ? `border-signal ${isOpen ? "ring-2 ring-signal/40" : ""}`
    : isOpen
      ? "border-transparent ring-2 ring-copper"
      : "border-[rgba(11,15,14,0.15)]";

  // Read-only orders render the picked name as plain text — there is nothing to
  // search, and a live dropdown over a closed record invites accidental edits.
  if (disabled) {
    return (
      <div
        className={`flex w-full items-center truncate rounded-md border border-[rgba(11,15,14,0.15)] bg-bone text-mist ${sizeCls}`}
      >
        {value || "—"}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2">
        <div
          className={`flex min-w-0 flex-1 items-center rounded-md border bg-white ${boxState}`}
        >
          <input
            ref={inputRef}
            type="text"
            // The box shows the selection at rest and the search term only
            // while open, so closing it discards whatever was typed.
            value={isOpen ? query : value}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={open}
            // Focusing does not fire again on a box that is already focused —
            // which it is after a pick — so clicking it has to open too.
            onClick={open}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                close();
              }
              if (event.key === "ArrowDown") open();
            }}
            placeholder={
              isOpen ? searchPlaceholder : (placeholder ?? searchPlaceholder)
            }
            autoComplete="off"
            className={`min-w-0 flex-1 bg-transparent text-ink outline-none placeholder-mist ${sizeCls}`}
          />

          {/* Both gated on the box being open: a closed box that briefly
              swapped its clear button for a spinner — which is what the
              post-Add refresh would do — reads as something going wrong. */}
          <div className="flex shrink-0 items-center gap-0.5 pr-1.5">
            {isOpen && loading && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-mist" />
            )}
            {!!value && !(isOpen && loading) && (
              <button
                type="button"
                // Keeps focus in the input so the box does not blur-close
                // between mousedown and click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClear}
                title={t("common:buttons.clear")}
                className="rounded p-0.5 text-mist hover:text-signal"
                tabIndex={-1}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => (isOpen ? close() : open())}
              className="rounded p-0.5 text-mist hover:text-ink"
              tabIndex={-1}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>

        {allowCreate && (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              close();
              setShowCreate(true);
            }}
            className={`shrink-0 rounded-md border border-[rgba(11,15,14,0.15)] bg-white px-3 font-medium text-ink shadow-sm transition-colors hover:bg-bone ${
              size === "sm" ? "h-8 text-[12px]" : "h-10 text-sm"
            }`}
          >
            {t("common:buttons.add")}
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-[rgba(11,15,14,0.12)] bg-white shadow-xl">
          <div className="max-h-60 overflow-y-auto">
            {loading && records.length === 0 ? (
              <p className="flex items-center justify-center gap-2 py-5 text-[12px] text-mist">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("common:directorySelect.loading")}
              </p>
            ) : records.length === 0 ? (
              <p className="py-5 text-center text-[12px] text-mist">
                {t("common:directorySelect.noResults")}
              </p>
            ) : (
              records.map((record) => (
                <button
                  key={record._id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(record)}
                  className={`block w-full border-b border-[rgba(11,15,14,0.06)] px-3 py-2 text-left last:border-b-0 hover:bg-bone ${
                    record._id === selectedId ? "bg-copper/10" : ""
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-mist" />
                    <span className="truncate text-[13px] font-medium text-ink">
                      {record.companyName}
                    </span>
                    {record.clientCode && (
                      <span className="shrink-0 text-[11px] text-mist">
                        ({record.clientCode})
                      </span>
                    )}
                  </span>
                  {addressLine(record) && (
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-mist">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{addressLine(record)}</span>
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {page < pages && (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => load(query, page + 1, true)}
              // Also blocked while page 1 is in flight: the page counter still
              // describes the previous search until that response lands.
              disabled={loadingMore || loading}
              className="flex w-full items-center gap-1.5 border-t border-[rgba(11,15,14,0.08)] px-3 py-2 text-left text-[12px] font-medium text-copper hover:bg-bone disabled:opacity-60"
            >
              {loadingMore && <Loader2 className="h-3 w-3 animate-spin" />}
              {t("common:directorySelect.loadMore")}
            </button>
          )}
        </div>
      )}

      {/*
       * The directory form is a <form> of its own and this component sits inside
       * the container order's form, so it is portalled out of that DOM tree —
       * but a portal only moves the DOM. React still propagates events along the
       * component tree, so the inner submit would arrive at the order's
       * onSubmit and save it (dropping the page back to read mode) every time a
       * partner was added. Stopping it here is the part the portal cannot do.
       *
       * Unmounting on close is what resets the form: PartnerForm holds its own
       * react-hook-form state, so the next Add always opens on a blank one.
       */}
      {showCreate &&
        createPortal(
          <div onSubmit={(event) => event.stopPropagation()}>
            <Modal
              isOpen
              onClose={closeCreate}
              title={t(
                source === "partners"
                  ? "common:directorySelect.addPartnerTitle"
                  : "common:directorySelect.addAddressTitle",
              )}
              size="lg"
            >
              <div className="flex-1 overflow-y-auto">
                <PartnerForm
                  onSubmit={handleCreate}
                  loading={creating}
                  // Mirrors what each directory's own page asks for: partners
                  // carry billing and the forwarder flag, addresses do not
                  // (their API rejects it).
                  requireRegistrationCode={source === "partners"}
                  withBilling={source === "partners"}
                  withForwarder={source === "partners"}
                  initialValues={createDefaults}
                />
              </div>
            </Modal>
          </div>,
          document.body,
        )}
    </div>
  );
};

/** Records saved before coordinates existed store nulls — that is "no location". */
const normalizeCoordinates = (
  coordinates?: { latitude?: number | null; longitude?: number | null } | null,
) =>
  typeof coordinates?.latitude === "number" &&
  typeof coordinates?.longitude === "number"
    ? { latitude: coordinates.latitude, longitude: coordinates.longitude }
    : null;

/** A directory record as the snapshot a form block is filled from. */
export const toSnapshot = (record: Partner): DirectorySelection => ({
  _id: record._id,
  companyName: record.companyName,
  registerationCode: record.registerationCode,
  phoneNumber: record.phoneNumber,
  emailAddress: record.emailAddress,
  clientCode: record.clientCode,
  address: {
    street: record.address?.street || "",
    city: record.address?.city || "",
    state: record.address?.state || "",
    postalCode: record.address?.postalCode || "",
    country:
      typeof record.address?.country === "object" && record.address?.country
        ? record.address.country._id
        : ((record.address?.country as unknown as string) ?? ""),
    coordinates: normalizeCoordinates(record.address?.coordinates),
  },
  isTemporary: false,
});

/** Street, city, state — whichever of them the record actually has. */
const addressLine = (record: Partner): string =>
  [record.address?.street?.split(",")[0], record.address?.city, record.address?.state]
    .filter(Boolean)
    .join(", ");

export default DirectorySelect;
